import { createReducer, on } from '@ngrx/store';
import {
  askForChallengeSuccess,
  initLoginForgotPassword,
  initLoginSignIn,
  loadUserAuthenticatedError,
  loadUserAuthenticatedSuccess,
  loadUserAuthenticatedUnenriched,
  postForgotPassword,
  postForgotPasswordError,
  postForgotPasswordSuccess,
  postLogin,
  postLoginAccountNotProvisioned,
  postLoginAccountStatusRefused,
  postLoginError,
  postLoginMfa,
  postLoginMfaError,
  postLoginMfaSuccess,
  postLoginSuccess,
  postLoginUnavailable,
  postTokens,
  postTokensError,
  postTokensSuccess,
  redeemSsoHandoff,
  redeemSsoHandoffError,
  redeemSsoHandoffSuccess,
  requestOtpChallenge,
  requestOtpChallengeError,
  requestOtpChallengeSuccess,
  verifyOtpChallenge,
  verifyOtpChallengeError,
  verifyOtpChallengeSuccess,
} from '../actions/authentication.action';
import { TokenDTO, TokensResponse } from '@brain-auth/models/auth-token-dto.model';

export default interface AuthenticationState {
  isLoginLoading: boolean;
  isLoginError: boolean;
  isForgotPasswordLoading: boolean;
  isForgotPasswordSuccess: boolean;
  userAuthenticated?: any;
  tokenDto?: TokenDTO;
  isTokensLoading: boolean;
  isTokensError: boolean;
  tokensResponse?: TokensResponse;
  isOtpLoading: boolean;
  otpError: string | null;
  isSsoRedeemLoading: boolean;
  isSsoRedeemError: boolean;
  /** BRN-2208: signed in, but this browser's token carries no usable BRAIN enrichment claims. */
  isSessionUnenriched: boolean;
  /** BRN-2208 L5 F1: the login itself was refused because the account is not provisioned — the
   *  credential was correct. Distinct from `isSessionUnenriched`: there is no session at all here,
   *  which is why the unenriched page must not tell this user to "sign out". */
  isAccountNotProvisioned: boolean;
  /** BRN-2208 L5 F1: the login could not be attempted or answered — 5xx or transport. NOT a
   *  credential verdict, and must never render as one. */
  isLoginUnavailable: boolean;
  /** BRN-2208 L6 L4-R: the account is ARCHIVED or BLOCKED (brain-iam's FAILURE_WRONG_USER_STATUS).
   *  The password was right and no password action can help, so this must never render as a
   *  credential verdict either — and unlike `isAccountNotProvisioned` it does not navigate, because
   *  the enrichment page is a different (and equally false) story for this user. */
  isAccountStatusRefused: boolean;
}

export const initialState: AuthenticationState = {
  isLoginLoading: false,
  isLoginError: false,
  isForgotPasswordLoading: false,
  isForgotPasswordSuccess: false,
  userAuthenticated: undefined,
  tokenDto: undefined,
  isTokensLoading: false,
  isTokensError: false,
  tokensResponse: undefined,
  isOtpLoading: false,
  otpError: null,
  isSsoRedeemLoading: false,
  isSsoRedeemError: false,
  isSessionUnenriched: false,
  isAccountNotProvisioned: false,
  isLoginUnavailable: false,
  isAccountStatusRefused: false,
};

/** BRN-2208 L5 F1: every fresh login attempt clears the previous attempt's DIAGNOSIS, so a stale
 *  "not provisioned", "unavailable" or "account status" banner can never survive into an unrelated
 *  later attempt. */
const clearedLoginDiagnosis = {
  isAccountNotProvisioned: false,
  isLoginUnavailable: false,
  isAccountStatusRefused: false,
} as const;

export const reducer = createReducer(
  initialState,

  on(initLoginSignIn, (state): AuthenticationState => ({ ...state, isLoginError: false, ...clearedLoginDiagnosis })),
  on(initLoginForgotPassword, (state): AuthenticationState => ({ ...state, isForgotPasswordSuccess: false })),

  on(postLogin, (state): AuthenticationState => ({ ...state, isLoginLoading: true, isLoginError: false, ...clearedLoginDiagnosis })),
  on(postLoginSuccess, (state): AuthenticationState => ({ ...state, isLoginLoading: false, isLoginError: false, ...clearedLoginDiagnosis })),
  on(postLoginError, (state): AuthenticationState => ({ ...state, isLoginLoading: false, isLoginError: true })),

  // BRN-2208 L5 F1 -- the load-bearing half of the fix is `isLoginError: false` on both. These are
  // refusals, but they are not credential verdicts, so the "user name or password is incorrect"
  // banner must NOT light up for either.
  on(postLoginAccountNotProvisioned, (state): AuthenticationState => ({
    ...state,
    isLoginLoading: false,
    isLoginError: false,
    isLoginUnavailable: false,
    isAccountStatusRefused: false,
    isAccountNotProvisioned: true,
  })),
  on(postLoginUnavailable, (state): AuthenticationState => ({
    ...state,
    isLoginLoading: false,
    isLoginError: false,
    isAccountNotProvisioned: false,
    isAccountStatusRefused: false,
    isLoginUnavailable: true,
  })),
  // BRN-2208 L6 L4-R -- same load-bearing `isLoginError: false`, one outcome over: an archived or
  // blocked account is a refusal, never a credential verdict.
  on(postLoginAccountStatusRefused, (state): AuthenticationState => ({
    ...state,
    isLoginLoading: false,
    isLoginError: false,
    isAccountNotProvisioned: false,
    isLoginUnavailable: false,
    isAccountStatusRefused: true,
  })),

  on(askForChallengeSuccess, (state, { tokenDto }): AuthenticationState => ({ ...state, isLoginLoading: false, isLoginError: false, tokenDto })),

  on(postLoginMfa, (state): AuthenticationState => ({ ...state, isLoginLoading: true, isLoginError: false, ...clearedLoginDiagnosis })),
  on(postLoginMfaSuccess, (state, { userAuthenticated }): AuthenticationState => ({ ...state, isLoginLoading: false, isLoginError: false, userAuthenticated })),
  on(postLoginMfaError, (state): AuthenticationState => ({ ...state, isLoginLoading: false, isLoginError: true })),

  on(postForgotPassword, (state): AuthenticationState => ({ ...state, isForgotPasswordLoading: true, isForgotPasswordSuccess: false })),
  on(postForgotPasswordSuccess, (state): AuthenticationState => ({ ...state, isForgotPasswordLoading: false, isForgotPasswordSuccess: true })),
  on(postForgotPasswordError, (state): AuthenticationState => ({ ...state, isForgotPasswordLoading: false, isForgotPasswordSuccess: true })),

  on(loadUserAuthenticatedSuccess, (state, { userAuthenticated }): AuthenticationState => ({ ...state, userAuthenticated, isSessionUnenriched: false })),
  on(loadUserAuthenticatedError, (state): AuthenticationState => ({ ...state, userAuthenticated: undefined, isSessionUnenriched: false })),
  // BRN-2208: there IS a signed-in user; what is missing is the enrichment, not the session.
  on(loadUserAuthenticatedUnenriched, (state): AuthenticationState => ({ ...state, userAuthenticated: undefined, isSessionUnenriched: true })),

  on(
    postTokens,
    (state): AuthenticationState => ({
      ...state,
      isTokensLoading: true,
      isTokensError: false,
    }),
  ),
  on(
    postTokensSuccess,
    (state, { response }): AuthenticationState => ({
      ...state,
      isTokensLoading: false,
      isTokensError: false,
      tokensResponse: response,
    }),
  ),
  on(
    postTokensError,
    (state): AuthenticationState => ({
      ...state,
      isTokensLoading: false,
      isTokensError: true,
    }),
  ),
  on(requestOtpChallenge, (state): AuthenticationState => ({ ...state, isOtpLoading: true, otpError: null })),
  on(requestOtpChallengeSuccess, (state): AuthenticationState => ({ ...state, isOtpLoading: false })),
  on(requestOtpChallengeError, (state, { error }): AuthenticationState => ({
    ...state,
    isOtpLoading: false,
    otpError: error?.message ?? 'Unable to send verification code',
  })),
  on(verifyOtpChallenge, (state): AuthenticationState => ({ ...state, isOtpLoading: true, otpError: null })),
  on(verifyOtpChallengeSuccess, (state): AuthenticationState => ({ ...state, isOtpLoading: false, otpError: null })),
  on(verifyOtpChallengeError, (state, { error }): AuthenticationState => ({
    ...state,
    isOtpLoading: false,
    otpError: error?.message ?? 'Invalid verification code',
  })),

  on(redeemSsoHandoff, (state): AuthenticationState => ({ ...state, isSsoRedeemLoading: true, isSsoRedeemError: false })),
  on(redeemSsoHandoffSuccess, (state): AuthenticationState => ({ ...state, isSsoRedeemLoading: false, isSsoRedeemError: false })),
  on(redeemSsoHandoffError, (state): AuthenticationState => ({ ...state, isSsoRedeemLoading: false, isSsoRedeemError: true })),
);
